// DTOs de WS-8 P1 — programación diaria por ORDEN DE PROCESO de Alpina
// (módulo Facturación del coordinador).
//
// Modelo (mig 00052): `programacion_op` es el lado PROGRAMADO del cruce.
// Lo producido NO se duplica — sale de registros_produccion_hora (capturas
// validadas) en P2. numero_op es NULLABLE: el Excel real de Alpina trae filas
// programadas sin OP (Orden='-') que igual suman al nivel de servicio.
//
// Endpoints P1:
//   GET    /api/coordinador/facturacion/programacion-op?desde&hasta&cliente_id&area_id
//   POST   /api/coordinador/facturacion/programacion-op            (alta manual)
//   POST   /api/coordinador/facturacion/programacion-op/importar   (filas parseadas en FE)
//   PATCH  /api/coordinador/facturacion/programacion-op/:id        (corrección con motivo)
//   DELETE /api/coordinador/facturacion/programacion-op/:id        (soft, con razón)
import { z } from 'zod';
import { opLoteOcRegex, opLoteOcMensaje } from './registro_produccion_hora.js';
/** OP de Alpina (8 dígitos) o null — fila programada sin OP asignada. */
const numeroOpSchema = z
    .string()
    .trim()
    .regex(opLoteOcRegex, opLoteOcMensaje)
    .nullable()
    .default(null);
// =============================================================================
// Fila persistida (response)
// =============================================================================
export const programacionOpSchema = z.object({
    id: z.string().uuid(),
    cliente_id: z.string().uuid(),
    cliente_nombre: z.string(),
    planta_id: z.string().uuid(),
    planta_nombre: z.string(),
    area_id: z.string().uuid(),
    area_codigo: z.string(),
    area_nombre: z.string(),
    fecha_programada: z.string().date(),
    numero_op: z.string().nullable(),
    sku_id: z.string().uuid(),
    sku_codigo_cliente: z.string(),
    sku_descripcion: z.string(),
    cantidad_programada: z.number().nonnegative(),
    observacion: z.string().nullable(),
    origen: z.enum(['excel_alpina', 'manual']),
    /** Badge de corrección (denormalizado, patrón mig 41). */
    corregido_at: z.string().nullable(),
    razon_correccion: z.string().nullable(),
    registrado_por_nombre: z.string(),
    registrado_por_apellido: z.string(),
    created_at: z.string(),
    updated_at: z.string(),
});
// =============================================================================
// GET list — filtros por rango de fechas / cliente / área
// =============================================================================
export const programacionOpListQuerySchema = z.object({
    desde: z.string().date(),
    hasta: z.string().date(),
    cliente_id: z.string().uuid().optional(),
    area_id: z.string().uuid().optional(),
});
export const programacionOpListSchema = z.object({
    desde: z.string().date(),
    hasta: z.string().date(),
    filas: z.array(programacionOpSchema),
    /** Recursos para form/import: clientes activos, áreas del scope, SKUs. */
    clientes: z.array(z.object({ id: z.string().uuid(), nombre: z.string(), es_principal: z.boolean() })),
    areas: z.array(z.object({
        id: z.string().uuid(),
        codigo: z.string(),
        nombre: z.string(),
        planta_id: z.string().uuid(),
        planta_nombre: z.string(),
    })),
    skus: z.array(z.object({
        id: z.string().uuid(),
        cliente_id: z.string().uuid(),
        codigo_cliente: z.string(),
        descripcion: z.string(),
        formato: z.string().nullable(),
    })),
    /** Mapa SKU→área en plantas del scope (el import deriva el área del SKU). */
    skus_areas: z.array(z.object({ sku_id: z.string().uuid(), area_id: z.string().uuid() })),
    /** Líneas del scope: el Excel de Alpina a veces trae el nombre de la LÍNEA
     * (ej. "FLOWPACK") en la columna de actividad — se resuelve al área de la línea. */
    lineas: z.array(z.object({
        id: z.string().uuid(),
        codigo: z.string(),
        nombre: z.string(),
        area_id: z.string().uuid(),
    })),
});
// =============================================================================
// POST alta manual
// =============================================================================
export const programacionOpCreateSchema = z.object({
    cliente_id: z.string().uuid(),
    area_id: z.string().uuid(),
    fecha_programada: z.string().date(),
    numero_op: numeroOpSchema,
    sku_id: z.string().uuid(),
    // 0 es válido: OP reprogramada/cancelada que se deja registrada (mig 53).
    cantidad_programada: z.number().nonnegative('No puede ser negativa'),
    observacion: z.string().trim().max(500).nullable().optional(),
});
// =============================================================================
// POST importar — filas ya parseadas/mapeadas en el FE (patrón C4: el FE mapea
// Material→sku_id y deriva el área; el API valida y persiste). Upsert por
// (cliente, planta, OP, SKU, fecha): recargar el mismo Excel actualiza cantidad
// y observación en vez de duplicar.
// =============================================================================
// Mensajes en español: desde el fix del incidente Sopó (2026-07-03) el FE
// muestra el issue de Zod en el toast cuando el preview dejó pasar algo.
const importFilaSchema = z.object({
    area_id: z.string().uuid(),
    fecha_programada: z.string().date('Fecha inválida — use AAAA-MM-DD (año-mes-día)'),
    numero_op: numeroOpSchema,
    sku_id: z.string().uuid(),
    cantidad_programada: z.number().nonnegative('No puede ser negativa'),
    observacion: z.string().trim().max(500, 'Máximo 500 caracteres').nullable().optional(),
});
export const programacionOpImportSchema = z.object({
    cliente_id: z.string().uuid(),
    filas: z.array(importFilaSchema).min(1, 'Mínimo 1 fila').max(2000, 'Máximo 2000 filas por carga'),
});
export const programacionOpImportResultSchema = z.object({
    insertadas: z.number().int().min(0),
    actualizadas: z.number().int().min(0),
});
// =============================================================================
// PATCH corrección — con motivo obligatorio (≥20, patrón correccion.ts)
// =============================================================================
export const programacionOpCorreccionSchema = z
    .object({
    fecha_programada: z.string().date().optional(),
    numero_op: z.string().trim().regex(opLoteOcRegex, opLoteOcMensaje).nullable().optional(),
    sku_id: z.string().uuid().optional(),
    area_id: z.string().uuid().optional(),
    cantidad_programada: z.number().nonnegative().optional(),
    observacion: z.string().trim().max(500).nullable().optional(),
    razon: z.string().trim().min(20, 'El motivo debe tener al menos 20 caracteres').max(500),
})
    .refine((v) => v.fecha_programada !== undefined ||
    v.numero_op !== undefined ||
    v.sku_id !== undefined ||
    v.area_id !== undefined ||
    v.cantidad_programada !== undefined ||
    v.observacion !== undefined, { message: 'Debe corregir al menos un campo' });
// =============================================================================
// DELETE soft — razón obligatoria
// =============================================================================
export const programacionOpDeleteSchema = z.object({
    razon: z.string().trim().min(5, 'La razón debe tener al menos 5 caracteres').max(500),
});
//# sourceMappingURL=coordinador_programacion_op.js.map